import React, { useState, useEffect } from 'react';
import { Gift, Clock, CheckCircle, XCircle, Info, Copy } from 'lucide-react';
import { getUserCoupons, getCouponInstructions, getCouponStats } from '../utils/couponManager';
import { useTranslation } from 'react-i18next';

const UserCoupons = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [userCoupons, setUserCoupons] = useState([]);
  const [couponStats, setCouponStats] = useState({
    total: 0,
    used: 0,
    active: 0,
    expired: 0
  });
  const [showAll, setShowAll] = useState(false);
  const [copiedCoupon, setCopiedCoupon] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const loadUserCoupons = async () => {
    try {
      console.log('Loading user coupons...');
      const [coupons, stats] = await Promise.all([
        getUserCoupons(),
        getCouponStats()
      ]);
      console.log('Loaded coupons:', coupons);
      console.log('Loaded stats:', stats);
      setUserCoupons(coupons);
      setCouponStats(stats);
    } catch (error) {
      console.error('Error loading user coupons:', error);
      // Set default values when API fails
      setUserCoupons([]);
      setCouponStats({
        total: 0,
        used: 0,
        active: 0,
        expired: 0
      });
    }
  };

  // Expose refresh function to parent components
  const refreshCoupons = () => {
    console.log('Manual refresh triggered');
    loadUserCoupons();
  };

  // Make refreshCoupons available globally
  React.useEffect(() => {
    window.refreshUserCoupons = refreshCoupons;
    return () => {
      delete window.refreshUserCoupons;
    };
  }, []);

  useEffect(() => {
    loadUserCoupons();

    // Refresh coupons every 30 seconds to catch new ones
    const interval = setInterval(loadUserCoupons, 30000);

    return () => clearInterval(interval);
  }, []);

  const copyCouponCode = (couponCode) => {
    navigator.clipboard.writeText(couponCode);
    setCopiedCoupon(couponCode);
    setTimeout(() => setCopiedCoupon(null), 2000);
  };

  const getCouponStatusColor = (coupon) => {
    if (coupon.used) return 'text-green-600';
    if (coupon.expiresAt && new Date(coupon.expiresAt) <= new Date()) return 'text-red-600';
    return 'text-blue-600';
  };

  const getCouponStatusIcon = (coupon) => {
    if (coupon.used) return <CheckCircle className="h-4 w-4" />;
    if (coupon.expiresAt && new Date(coupon.expiresAt) <= new Date()) return <XCircle className="h-4 w-4" />;
    return <Clock className="h-4 w-4" />;
  };

  const getCouponStatusText = (coupon) => {
    if (coupon.used) return lang === 'ar' ? 'مستخدم' : 'Used';
    if (coupon.expiresAt && new Date(coupon.expiresAt) <= new Date()) return lang === 'ar' ? 'منتهي الصلاحية' : 'Expired';
    return lang === 'ar' ? 'نشط' : 'Active';
  };

  const displayCoupons = showAll ? userCoupons : userCoupons.slice(0, 3);

  // Show the component even when there are no coupons, but with a different message

  return (
    <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 mb-8 border border-red-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="relative flex-shrink-0">
            <Gift className="h-10 w-10 md:h-12 md:w-12 text-red-600" />
            <div className="absolute inset-0 h-10 w-10 md:h-12 md:w-12 bg-red-400 rounded-full blur-lg opacity-30"></div>
          </div>
          <div>
            <h3 className="text-xl md:text-3xl font-bold text-red-800">{t('coupons.title')}</h3>
            <p className="text-sm md:text-base text-red-600">{t('coupons.rewardsAndSavings')}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Coupon Stats */}
          <div className="flex items-center gap-2 text-xs md:text-sm">
            <div className="bg-white px-3 py-1.5 rounded-full border border-red-100 shadow-sm">
              <span className="text-red-700 font-bold">{couponStats.active || 0} {t('coupons.active')}</span>
            </div>
            <div className="bg-white px-3 py-1.5 rounded-full border border-green-100 shadow-sm">
              <span className="text-green-700 font-bold">{couponStats.used || 0} {t('coupons.used')}</span>
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-1 sm:flex-none bg-red-600 text-white px-5 py-2.5 rounded-xl hover:bg-red-700 transition-all duration-300 font-bold flex items-center justify-center space-x-2 shadow-lg active:scale-95"
          >
            <span>{isExpanded ? t('coupons.minimize') : t('coupons.viewAll')}</span>
            <span className="text-xl leading-none">{isExpanded ? '−' : '+'}</span>
          </button>
        </div>
      </div>

      {/* Expandable Coupons Content */}
      {isExpanded && (
        <>
          {/* Coupons Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {displayCoupons.map((coupon) => (
              <div
                key={coupon.id}
                className={`bg-white rounded-xl p-4 border-2 transition-all duration-300 hover:shadow-lg ${coupon.used
                  ? 'border-green-200 bg-green-50'
                  : coupon.expiresAt && new Date(coupon.expiresAt) <= new Date()
                    ? 'border-red-200 bg-red-50'
                    : 'border-red-200 hover:border-red-300'
                  }`}
              >
                {/* Coupon Header */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center space-x-1.5">
                    {getCouponStatusIcon(coupon)}
                    <span className={`text-xs font-bold uppercase tracking-wider ${getCouponStatusColor(coupon)}`}>
                      {getCouponStatusText(coupon)}
                    </span>
                  </div>
                  <div className="text-[10px] font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {coupon.acquiredAt && new Date(coupon.acquiredAt).toLocaleDateString()}
                  </div>
                </div>

                {/* Coupon Code */}
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-mono font-bold text-red-800 bg-red-100 px-3 py-1 rounded-lg">
                      {coupon.code}
                    </span>
                    {!coupon.used && (
                      <button
                        onClick={() => copyCouponCode(coupon.code)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Copy coupon code"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {copiedCoupon === coupon.code && (
                    <p className="text-xs text-green-600 mt-1">{t('coupons.copiedToClipboard')}</p>
                  )}
                </div>

                {/* Coupon Details */}
                <div className="mb-3">
                  <div className="text-lg font-bold text-red-800 mb-1">
                    {coupon.type === 'percentage' && t('coupons.percentOff', { discount: coupon.discount })}
                    {coupon.type === 'fixed' && t('coupons.fixedOff', { discount: coupon.discount })}
                    {coupon.type === 'delivery' && t('coupons.freeDelivery')}
                  </div>
                  <p className="text-sm text-gray-600">
                    {lang === 'ar' ? (coupon.text_ar || coupon.text || coupon.description) : (coupon.text || coupon.description)}
                  </p>
                </div>

                {/* Instructions */}
                <div className="space-y-1">
                  {getCouponInstructions(coupon, lang).map((instruction, index) => (
                    <div key={index} className="flex items-center space-x-2 text-xs">
                      <Info className="h-3 w-3 text-gray-500" />
                      <span className="text-gray-600">{instruction}</span>
                    </div>
                  ))}
                </div>

                {/* How to Use */}
                {!coupon.used && (
                  <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-800 font-medium mb-1">{t('coupons.howToUse')}</p>
                    <p className="text-xs text-blue-700">{t('coupons.howToUseDesc')}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Show More/Less Button */}
          {userCoupons.length > 3 && (
            <div className="text-center">
              <button
                onClick={() => setShowAll(!showAll)}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                {showAll ? t('coupons.showLess') : t('coupons.showAll', { count: userCoupons.length })}
              </button>
            </div>
          )}

          {/* Empty State */}
          {userCoupons.length === 0 && (
            <div className="text-center py-8">
              <Gift className="h-16 w-16 text-red-300 mx-auto mb-4" />
              <p className="text-red-600 mb-2 font-semibold text-lg">{t('coupons.noCoupons')}</p>
              <p className="text-sm text-red-500 mb-4">{t('coupons.noCouponsSubtitle')}</p>
              <div className="bg-red-100 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-red-700">
                  💡 {t('coupons.tipText')}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserCoupons; 